import React, { useState } from "react";
import PropTypes from "prop-types";
import { List } from "antd";
import TableWithControls from "../../common/table-with-controls";
import { getAssets, esqAssets } from "../../../services/api-landscape";
import { toDateString, toGooglePatentLink, toSetString, toPatentDetailLink, toUSDCurrency } from "../../../helpers/converters";
import { diversifyHighlight } from "../../../helpers/table";
import { withUser } from "@unifiedpatents/sso";
import { isFinite } from "lodash";

const PatentList = ({ filter = {}, esUrl, version }) => {
  const [queries, setQueries] = useState({ sort: "-alpha_score", ...filter });
  const onQueryChange = queriesNew => setQueries(({ sort: "-alpha_score", ...queriesNew }));
  const fetcher = q => getAssets(esUrl, version, { ...q }).then(json => ({
    total: json.hits.total.value,
    data: json.hits.hits.map(e => ({ ...e._source, highlight: e.highlight }))
  }))
  const allCols = [
    {
      dataIndex: "publication_number",
      title: "Pub. Number",
      width: 200,
      render: (val) =>
        val && (
          <span>
            {toPatentDetailLink(val)}
            {' '}
            {toGooglePatentLink(val)}
          </span>
        )
    },
    { dataIndex: "publication_status", title: "Publication Status", width: 120 },
    { dataIndex: "publication_type", title: "Publication Type", width: 120, sorter: true, render: (val) => val === "G" ? "Grant" : val === "A" ? "Application" : ""},
    { dataIndex: "doc_category", title: "Doc Category", width: 120 },
    { dataIndex: "country", title: "Country", width: 100 },
    { dataIndex: "kind_code", title: "Kind Code", width: 120 },
    { dataIndex: "family_id", title: "Family ID", width: 120 },
    { dataIndex: "extended_family_id", title: "Extended Family ID", width: 160 },
    { dataIndex: "application_number", title: "Application Number", width: 200 },
    { dataIndex: "assignee_current", title: "Current Assignee", render: toSetString, width: 200, sorter: true },
    { dataIndex: "assignee_original", title: "Original Assignee", width: 200 },
    { dataIndex: "assignee_parent", title: "Parent Company", width: 200 },
    { dataIndex: "grant_number", title: "Patent Number", width: 160 },
    { dataIndex: "examiner", title: "Examiner", width: 200, render: toSetString },
    { dataIndex: "inventors", title: "Inventors", render: toSetString, width: 200 },
    {
      dataIndex: "annuities",
      title: "Lifetime Renewal Fees",
      width: 80,
      sorter: true,
      render: val => (isFinite(val) ? toUSDCurrency(Math.round(val / 10) * 10) : "")
    },
    {
      dataIndex: "norm_family_annuities",
      title: "SVIX",
      width: 80,
      render: val => (isFinite(val) ? val.toFixed(2) : "0.00")
    },
    { dataIndex: "title", title: "Title", width: 600 },
    { dataIndex: "cpc_codes", title: "CPC Codes", width: 140, render: toSetString },
    { dataIndex: "uspc_codes", title: "USPC Codes", width: 140, render: toSetString },
    { dataIndex: "application_date", title: "Filing Date", width: 140 },
    { dataIndex: "expiration_date", title: "Expiration Date", width: 140 },
    { dataIndex: "priority_date", title: "Priority Date", render: toDateString, width: 140, sorter: true },
    { dataIndex: "publication_date", title: "Publication Date", render: toDateString, width: 140, sorter: true },
    { dataIndex: "rating_broadness", title: "BRIX", width: 80, sorter: true },
    { dataIndex: "rating_citation", title: "CITX", width: 80, sorter: true },
    {
      dataIndex: "rnix_score",
      title: "RNIX",
      width: 80,
      sorter: true,
      render: val => (isFinite(val) ? val.toFixed(2) : "")
    },
    {
      dataIndex: "portfolio_score",
      title: "PVIX",
      width: 80,
      render: val => (val ? val.toFixed(2) : ""),
      sorter: true,
    },
    // make sure to include hardcoded .key for versioned fields to disable dynamic construction
    { dataIndex: [version, "alpha_score"], title: "Similarity", key: "alpha_score", width: 125, render: val => val.toFixed(2), sorter: true },
    { dataIndex: [version, "pagerank"], title: "PatentRank", key: "pagerank", width: 125, render: val => (val ? (val * 100).toFixed(2) : 0), sorter: true },
    { dataIndex: [version, "pools"], title: "Collections", key: "pools", width: 120, render: toSetString },
    { dataIndex: [version, "techs"], title: "Technology", key: "techs", width: 200, render: toSetString },
  ];
  const specCols = version.startsWith("lte") || version.startsWith("5g") ? [
    // don't include .key for spec fields to enable dynamic construction
    { dataIndex: ["3gpp", "spec"], title: "Specification", width: 140, render: toSetString },
    { dataIndex: ["3gpp", "spec_series"], title: "Specification Series", width: 140, render: toSetString },
    { dataIndex: ["3gpp", "declarations"], title: "Declarations", width: 140, render: toSetString },
    { dataIndex: ["3gpp", "2g_declaration_date"], title: "2G Declaration Date", width: 140, render: toDateString },
    { dataIndex: ["3gpp", "3g_declaration_date"], title: "3G Declaration Date", width: 140, render: toDateString },
    { dataIndex: ["3gpp", "4g_declaration_date"], title: "4G Declaration Date", width: 140, render: toDateString },
    { dataIndex: ["3gpp", "5g_declaration_date"], title: "5G Declaration Date", width: 140, render: toDateString },
  ] : [];
  const highlightFieldName = {
    description: "DESCRIPTION",
    claims: "CLAIMS",
    abstract_fulltext: "ABSTRACT",
    title: "TITLE",
    non_patent_citations: "CITATIONS"
  }
  const expandable = queries.full_text ? {
    defaultExpandAllRows: true,
    expandedRowRender: record => (
      <List
        dataSource={diversifyHighlight(Object.keys(highlightFieldName))(record)}
        renderItem={d => (
          <List.Item>
            <div dangerouslySetInnerHTML={{ __html: `[${highlightFieldName[d.field]}] ${d.highlight}` }} />
          </List.Item>
        )}
        size="small"
      />
    )
  } : undefined;
  return (
    <TableWithControls
      queries={queries}
      fetcher={fetcher}
      onChange={onQueryChange}
      columns={[...allCols, ...specCols]}
      columnKeysSelected={["publication_number", "assignee_current", "publication_type", "priority_date", "publication_date", "title", "alpha_score", "portfolio_score"]}
      rowKey="publication_number"
      tableKey={`landscape${version}Table`}
      expandable={expandable}
      size="small"
      shouldSelectColumns={true}
      shouldExport={true}
      exportESUrl={esUrl}
      exportESQueryBuilder={q => esqAssets(version, q)}
    />
  )
}

PatentList.propTypes = {
  filter: PropTypes.object,
  esUrl: PropTypes.string.isRequired,
  version: PropTypes.string.isRequired
};

export default withUser(PatentList);
